import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import { DEFAULT_MEDIA_QUERY_PLATFORMS } from 'common/constants/story-media-query';
import { getMediaKeysInOrderOfInheritance } from 'common/utils/get-media-keys-in-order-of-inheritance';
import { getPlatformReferenceWidth, orderMediaQueryConfig } from 'common/utils/story-media-query';
import { convertPxToVw } from 'utils/helpers';
import {
	DESIGN_PRESET_PROPERTIES,
	type DesignDefinition,
	type DesignToken,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetPropertyValue,
	type DesignPresetValue,
} from './types';
import { copyDesignDefinition } from './operations';
import { isDesignTokenTypeCompatible } from './properties';
import { normalizeDesignTokenValue } from './values';
import { parseDesignPresetShadows } from './shadows';
import { parseDesignPresetBorderStyles } from './borders';
import { resolveDesignModeValue, getDefaultDesignModeSelection } from './modes';

export const DESIGN_PRESET_ENUM_VALUES = {
	textTransform: ['capitalize', 'uppercase', 'lowercase', 'none'],
	textDecoration: ['none', 'underline', 'overline', 'line-through'],
	textAlign: ['left', 'center', 'right'],
	direction: ['ltr', 'rtl'],
	borderStyle: [
		'none',
		'solid',
		'dashed',
		'dotted',
		'solid none none none',
		'none solid none none',
		'none none solid none',
		'none none none solid',
		'none solid none',
		'solid none',
	],
} as const;
const REFERENCE = /^var\(--sc-token-([a-z][a-z0-9-]*)\)$/;
const PLATFORMS = Object.values(DEFAULT_MEDIA_QUERY_PLATFORMS).filter(value => value !== 'default');
export function getDesignPresetTokenId(value: unknown): string | undefined {
	return typeof value === 'string' ? value.match(REFERENCE)?.[1] : undefined;
}
export function normalizeDesignPresetValue(property: DesignPresetProperty, value: unknown): DesignPresetValue {
	if (!DESIGN_PRESET_PROPERTIES.includes(property))
		throw new Error(`Unsupported design preset property: ${property}.`);
	if (getDesignPresetTokenId(value)) {
		if (
			isDesignTokenTypeCompatible(property, 'color') ||
			isDesignTokenTypeCompatible(property, 'dimension') ||
			isDesignTokenTypeCompatible(property, 'number') ||
			isDesignTokenTypeCompatible(property, 'fontFamily')
		)
			return String(value);
		throw new Error(`Tokens are not supported for ${property}.`);
	}
	if (property === 'borderStyle') {
		if (typeof value !== 'string') throw new Error('Invalid borderStyle value.');
		parseDesignPresetBorderStyles(value);
		return value.trim().replace(/\s+/g, ' ');
	}
	if (property in DESIGN_PRESET_ENUM_VALUES) {
		const values: readonly string[] = DESIGN_PRESET_ENUM_VALUES[property as keyof typeof DESIGN_PRESET_ENUM_VALUES];
		if (typeof value !== 'string' || !values.includes(value)) throw new Error(`Invalid ${property} value.`);
		return value;
	}
	if (property === 'lineHeight' && typeof value === 'number') return normalizeDesignTokenValue('number', value);
	if (property === 'opacity') {
		const opacity = normalizeDesignTokenValue('number', value);
		if (opacity < 0 || opacity > 1) throw new Error('Opacity must be between 0 and 1.');
		return opacity;
	}
	if (property === 'boxShadow' || property === 'textShadow') {
		parseDesignPresetShadows(property, value);
		return String(value).trim();
	}
	if (property === 'fontWeight') {
		const weight =
			typeof value === 'string' && /^(normal|bold)$/.test(value) ? (value === 'bold' ? 700 : 400) : value;
		const number = typeof weight === 'string' && /^\d+(\.\d+)?$/.test(weight) ? Number(weight) : weight;
		const normalized = normalizeDesignTokenValue('number', number);
		if (normalized < 1 || normalized > 1000) throw new Error('Font weight must be between 1 and 1000.');
		return normalized;
	}
	const type =
		property === 'fontFamily'
			? 'fontFamily'
			: property === 'color' || property === 'backgroundColor' || property === 'borderColor'
				? 'color'
				: 'dimension';
	return normalizeDesignTokenValue(type, value);
}
export function validateDesignPresets(input: unknown): DesignPreset[] {
	if (!Array.isArray(input)) throw new Error('Design presets require an array.');
	const ids = new Set<string>();
	return input.map((preset: DesignPreset) => {
		if (
			!preset ||
			typeof preset.id !== 'string' ||
			!/^[a-z][a-z0-9-]*$/.test(preset.id) ||
			ids.has(preset.id) ||
			typeof preset.name !== 'string' ||
			!preset.name.trim() ||
			(preset.description !== undefined && typeof preset.description !== 'string') ||
			!preset.properties ||
			typeof preset.properties !== 'object' ||
			Array.isArray(preset.properties)
		)
			throw new Error('Design presets require unique valid IDs, names and properties.');
		ids.add(preset.id);
		const properties: DesignPreset['properties'] = {};
		for (const [key, assignment] of Object.entries(preset.properties)) {
			const property = key as DesignPresetProperty;
			if (
				!DESIGN_PRESET_PROPERTIES.includes(property) ||
				!assignment ||
				typeof assignment !== 'object' ||
				Array.isArray(assignment) ||
				Object.keys(assignment).some(item => item !== 'base' && item !== 'platforms')
			)
				throw new Error(`Invalid design preset property: ${key}.`);
			const next: DesignPresetPropertyValue = {};
			if (assignment.base !== undefined) next.base = normalizeDesignPresetValue(property, assignment.base);
			if (assignment.platforms !== undefined) {
				if (
					!assignment.platforms ||
					typeof assignment.platforms !== 'object' ||
					Array.isArray(assignment.platforms)
				)
					throw new Error(`Invalid platforms for ${key}.`);
				const platforms: DesignPresetPropertyValue['platforms'] = {};
				for (const [platform, value] of Object.entries(assignment.platforms)) {
					if (!PLATFORMS.includes(platform as StoryDefaultPlatform))
						throw new Error(`Invalid design preset platform: ${platform}.`);
					platforms[platform as StoryDefaultPlatform] = normalizeDesignPresetValue(property, value);
				}
				if (Object.keys(platforms).length) next.platforms = platforms;
			}
			if (next.base === undefined && !Object.keys(next.platforms ?? {}).length)
				throw new Error(`${key} requires at least one assignment.`);
			properties[property] = next;
		}
		return {
			id: preset.id,
			name: preset.name,
			...(preset.description !== undefined ? { description: preset.description } : {}),
			properties,
		};
	});
}

/** Shared authored px assignment: convert only the authored platform, exactly once. */
export function normalizePlatformStyleValue(
	property: string,
	value: unknown,
	mediaQuery: StoryMediaQuery,
	platform: StoryDefaultPlatform
): unknown {
	return typeof value === 'string' &&
		/^[-+]?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(value.trim()) &&
		isDesignTokenTypeCompatible(property, 'dimension')
		? convertPxToVw(value, 2, getPlatformReferenceWidth(mediaQuery, platform))
		: value;
}
export function getDesignPresetInheritedAssignment(
	assignment: DesignPresetPropertyValue | undefined,
	platform: StoryDefaultPlatform,
	mediaQuery: StoryMediaQuery
): { value: DesignPresetValue; source: string } | undefined {
	if (!assignment) return undefined;
	if (!mediaQuery.config[platform]) {
		const value = assignment.platforms?.[platform];
		return value === undefined ? undefined : { value, source: platform };
	}
	const keys = getMediaKeysInOrderOfInheritance(
		mediaQuery.defaultPlatform,
		mediaQuery.config[platform],
		orderMediaQueryConfig(mediaQuery, { defaultFirst: true, minOrder: 'asc', maxOrder: 'desc' })
	);
	let inherited: ReturnType<typeof getDesignPresetInheritedAssignment>;
	for (const key of keys) {
		if (key === mediaQuery.defaultPlatform && assignment.base !== undefined)
			inherited = { value: assignment.base, source: 'Base' };
		const value = assignment.platforms?.[key as StoryDefaultPlatform];
		if (value !== undefined) inherited = { value, source: key };
	}
	return inherited;
}
function presetTokens(design: DesignDefinition): Map<string, DesignToken> {
	return new Map(
		design.tokens.collections.flatMap(collection => collection.tokens.map(token => [token.id, token] as const))
	);
}
export function getDesignPresetDependencies(preset: DesignPreset): string[] {
	return [
		...new Set(
			Object.values(preset.properties)
				.flatMap(assignment => [assignment.base, ...Object.values(assignment.platforms ?? {})])
				.map(getDesignPresetTokenId)
				.filter((id): id is string => !!id)
		),
	];
}
export function importDesignPreset(
	story: DesignDefinition,
	library: DesignDefinition,
	presetId: string
): DesignDefinition {
	const next = copyDesignDefinition(story);
	const local = next.presets?.find(preset => preset.id === presetId);
	const sourcePreset = library.presets?.find(preset => preset.id === presetId);
	if (!local && !sourcePreset) throw new Error(`Unknown library design preset: ${presetId}.`);
	const preset = validateDesignPresets([local ?? sourcePreset!])[0];
	if (!local) next.presets = [...(next.presets ?? []), preset];
	const tokens = presetTokens(next);
	for (const id of getDesignPresetDependencies(preset)) {
		const collection = library.tokens.collections.find(item => item.tokens.some(token => token.id === id));
		const source = collection?.tokens.find(token => token.id === id);
		const existing = tokens.get(id);
		if (existing) {
			if (source && source.type !== existing.type) throw new Error(`Token type conflict: ${id}.`);
			continue;
		}
		if (!source || !collection) throw new Error(`Missing design preset dependency: ${id}.`);
		let target = next.tokens.collections.find(item => item.id === collection.id);
		if (!target) {
			target = { id: collection.id, name: collection.name, tokens: [] };
			next.tokens.collections.push(target);
		}
		const token = structuredClone(source);
		target.tokens.push(token);
		tokens.set(id, token);
	}
	// Reject imported mode conditions against Story groups, without importing new mode groups.
	return copyDesignDefinition(next);
}
export function compileDesignPresetsForAI(design: DesignDefinition) {
	const tokens = presetTokens(design);
	return (design.presets ?? []).map(preset => ({
		...structuredClone(preset),
		resolvedPreview: Object.fromEntries(
			Object.entries(preset.properties).map(([property, assignment]) => {
				const resolve = (value: DesignPresetValue) => {
					const token = tokens.get(getDesignPresetTokenId(value) ?? '');
					return token
						? resolveDesignModeValue<string | number>(
								token.value,
								getDefaultDesignModeSelection(design.modeGroups)
							)
						: value;
				};
				return [
					property,
					{
						...(assignment.base !== undefined ? { base: resolve(assignment.base) } : {}),
						...(assignment.platforms
							? {
									platforms: Object.fromEntries(
										Object.entries(assignment.platforms).map(([platform, value]) => [
											platform,
											resolve(value),
										])
									),
								}
							: {}),
					},
				];
			})
		),
		dependencies: getDesignPresetDependencies(preset).map(id => {
			const token = tokens.get(id);
			return {
				id,
				reference: `var(--sc-token-${id})`,
				...(token ? { type: token.type, value: token.value } : { unresolved: true }),
			};
		}),
	}));
}
